import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AnimatePresence } from 'framer-motion';

// Pages
import Login from './pages/Auth/Login';
// import Register from './pages/Auth/Register';
import Dashboard from './pages/Dashboard/Dashboard';
import Products from './pages/Products/Products';
import Discovery from './pages/Discovery/Discovery';
import Leads from './pages/Leads/Leads';
import LeadDetail from './pages/Leads/LeadDetail';
import Quotes from './pages/Quotes/Quotes';
import QuoteDetail from './pages/Quotes/QuoteDetail';
import Deals from './pages/Deals/Deals';
import DealDetail from './pages/Deals/DealDetail';
import Organizations from './pages/Organizations/Organizations';
import Clients from './pages/Clients/Clients';
import Users from './pages/Users';
import Activity from './pages/Activity';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings/Settings';
import NotFound from './pages/NotFound';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              containerClassName="toast-container"
              toastOptions={{
                duration: 4000,
                className: 'dark:!bg-surface-800 dark:!text-gray-100 dark:!border-surface-700',
                style: {
                  background: 'var(--surface-primary)',
                  color: 'var(--text-primary)',
                  borderRadius: '0.75rem',
                  border: '1px solid var(--border-default)',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                  fontSize: '0.875rem',
                  padding: '12px 16px',
                },
                success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <AnimatePresence mode="wait">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              {/* Registration temporarily disabled - redirect to login */}
              <Route path="/register" element={<Navigate to="/login" replace />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="discovery" element={<Discovery />} />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="quotes" element={<Quotes />} />
              <Route path="quotes/:id" element={<QuoteDetail />} />
              <Route path="deals" element={<Deals />} />
              <Route path="deals/:id" element={<DealDetail />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="clients" element={<Clients />} />
              <Route path="users" element={<Users />} />
              <Route path="activity" element={<Activity />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </AnimatePresence>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
